import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Image, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { StyledLoading } from 'src/common/components';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { Achievement, SocialName } from 'src/common/page-share';
import { socialTrackEvents } from 'src/common/page-share/page-share.constants';
import { useSocialLinks } from 'src/common/page-share/use-social-links';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { StyledSocialButton } from 'src/common/styles';
import type { SegmentShareEvent } from 'src/common/types';
import { useGenerateBadgeImageMutation } from 'src/features/public-profile/public-profile.slice';
import { StyledBadgeContent, StyledBadgeTitle } from '../../../awards-badge-popup/awards-badge-popup.styles';
import { StyledContentWrapper, StyledSubTitle, StyledTitle } from '../../congratulations-full-screen.styles';
import { StyledButtonLink, StyledGenerateButton, StyledSocialButtonsWrapper, StyledSocialWrapper } from './sharing-badge-step.styles';
import type { ShareBadgeProps } from './sharing-badge-step.types';
export const SharingBadgeStep = ({
  awardedBadge,
  roomCode,
  isLoading,
  socialLinks = {
    [SocialName.facebook]: true,
    [SocialName.twitter]: true,
    [SocialName.linkedin]: true
  }
}: ShareBadgeProps) => {
  const [trackEvent] = useTrackEventMutation();
  const {
    data: userData
  } = useGetUserQuery();
  const userName = userData?.data?.user?.username ?? '';
  const [generateBadgeImage, {
    isLoading: isGeneratingBadgeImage
  }] = useGenerateBadgeImageMutation();
  const [isShareBadgeDisabled, setIsShareBadgeDisabled] = useState(true);
  const filteredLinks = useSocialLinks(socialLinks);
  const badgeUrl = `https://tryhackme.com/${userName ?? ''}/badges/${awardedBadge?.name ?? ''}`;
  const trackShareClick = useCallback((event: SegmentShareEvent) => {
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'social-share',
      properties: {
        page: 'congratulations-page',
        achievement: Achievement.badge,
        name: awardedBadge?.name,
        medium: event.split('-').slice(1).join('-'),
        'page-referrer': roomCode
      }
    });
  }, [trackEvent, roomCode, awardedBadge?.name]);
  const handleGenerateBadgeImage = async (badgeName: string): Promise<void> => {
    try {
      await generateBadgeImage({
        badgeName
      }).unwrap();
      setIsShareBadgeDisabled(false);
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'Something went wrong.');
    }
  };
  const handleGenerateClick = () => {
    if (awardedBadge?.name) {
      // eslint-disable-next-line no-void
      void handleGenerateBadgeImage(awardedBadge.name);
    }
  };
  return <StyledContentWrapper data-content="badge-sharing" data-sentry-element="StyledContentWrapper" data-sentry-component="SharingBadgeStep" data-sentry-source-file="sharing-badge-step.tsx">
      <header>
        <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="sharing-badge-step.tsx">Badge Earned!</StyledTitle>
        <StyledSubTitle data-sentry-element="StyledSubTitle" data-sentry-source-file="sharing-badge-step.tsx">
          Your dedication just paid off! You&apos;ve earned the {awardedBadge?.title} badge. Keep pushing forward and
          collect even more achievements as you progress. The next challenge awaits!
        </StyledSubTitle>
      </header>
      <StyledBadgeContent data-modal="congratulations" data-sentry-element="StyledBadgeContent" data-sentry-source-file="sharing-badge-step.tsx">
        {isLoading ? <>
            <StyledLoading width="255px" height="300px" data-testid="loading-image" />
            <StyledLoading width="180px" height="50px" />
          </> : <>
            <Image src={`https://assets.tryhackme.com${awardedBadge?.image ?? ''}`} width="255" height="300" />
            <div>
              <StyledBadgeTitle data-modal="congratulations">{awardedBadge?.title}</StyledBadgeTitle>
              <p data-modal="congratulations">{awardedBadge?.description}</p>
            </div>
          </>}
      </StyledBadgeContent>
      <StyledSocialWrapper data-sentry-element="StyledSocialWrapper" data-sentry-source-file="sharing-badge-step.tsx">
        <p>Share your badge:</p>
        {!!isShareBadgeDisabled && <StyledGenerateButton isLoading={isGeneratingBadgeImage} isDisabled={!isShareBadgeDisabled} loadingMsg="Generating..." onClick={handleGenerateClick}>
            Generate
          </StyledGenerateButton>}

        <StyledSocialButtonsWrapper data-sentry-element="StyledSocialButtonsWrapper" data-sentry-source-file="sharing-badge-step.tsx">
          {filteredLinks.map(social => <StyledSocialButton data-social={social.name} target="_blank" href={social.href({
          url: badgeUrl,
          description: awardedBadge?.description ?? '',
          campaign: {
            utmContent: 'badge'
          }
        })} onClick={() => trackShareClick(socialTrackEvents[social.name])} $isDisabled={isShareBadgeDisabled}>
              <social.icon />
              {social.officialName}
            </StyledSocialButton>)}
          <StyledButtonLink as={Link} to={`/${userName}/badges/${awardedBadge?.name ?? ''}`} variant="outlined" color="secondary" data-btn="link" target="_blank" $isDisabled={isShareBadgeDisabled} data-sentry-element="StyledButtonLink" data-sentry-source-file="sharing-badge-step.tsx">
            <FontAwesomeIcon icon={faLink} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="sharing-badge-step.tsx" />
            Go to link
          </StyledButtonLink>
        </StyledSocialButtonsWrapper>
      </StyledSocialWrapper>
    </StyledContentWrapper>;
};